import { inherits as _inherits, classCallCheck as _classCallCheck, possibleConstructorReturn as _possibleConstructorReturn, getPrototypeOf as _getPrototypeOf, defineProperty as _defineProperty, assertThisInitialized as _assertThisInitialized, objectSpread as _objectSpread, createClass as _createClass } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { createRef, Component } from 'react';
import PropTypes from 'prop-types';
import { setFocus, eventBus } from '@piggybank/core';
import pick from 'lodash/fp/pick';
import omit from 'lodash/fp/omit';
import { Provider } from './context.js';
import get from 'lodash/fp/get';
import isPlainObject from 'lodash/fp/isPlainObject';
import WizardPage from '../WizardPage/WizardPage.js';

var Wizard = /*#__PURE__*/function (_Component) {
  _inherits(Wizard, _Component);

  function Wizard(props) {
    var _this;

    _classCallCheck(this, Wizard);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(Wizard).call(this, props)); // Default to first page

    _defineProperty(_assertThisInitialized(_this), "componentDidMount", function () {
      setFocus(_this.ref.current);
      window.scrollTo(0, 0);

      _this.onNavigate();
    });

    _defineProperty(_assertThisInitialized(_this), "componentDidUpdate", function (prevProps, prevState) {
      if (_this.state.currentPage !== prevState.currentPage) {
        // Set focus only after page transition
        setFocus(_this.ref.current);
        window.scrollTo(0, 0);
      }
    });

    _defineProperty(_assertThisInitialized(_this), "componentWillUnmount", function () {
      if (_this.props.history) {
        _this.unlisten();
      }
    });

    _defineProperty(_assertThisInitialized(_this), "handleHistoryListen", function (location, action) {
      var _this$state = _this.state,
          currentPage = _this$state.currentPage,
          lastValidPage = _this$state.lastValidPage;
      var newPageIndex = location.state ? location.state.currentPage : 0;

      if (action === 'POP' && newPageIndex >= lastValidPage) {
        // When navigating with forward and back browser buttons always force user to lowest of currentPage and lastValidPage
        newPageIndex = Math.min(currentPage, lastValidPage);
      } else {
        // Otherwise when using Navigation increment lastValidPage every time a page is
        // successfully completed (and validated) by the user
        if (lastValidPage !== null && newPageIndex > lastValidPage + 1) {
          newPageIndex = lastValidPage;
        }
      }

      if (currentPage !== newPageIndex) {
        _this.setState({
          currentPage: newPageIndex,
          lastVisitedPage: currentPage
        });
      }
    });

    _defineProperty(_assertThisInitialized(_this), "setValues", function (values, callback) {
      _this.unsubmittedValues = omit(Object.keys(values), _this.unsubmittedValues);

      _this.setState(function (state) {
        return {
          values: _objectSpread({}, state.values, values)
        };
      }, callback);
    });

    _defineProperty(_assertThisInitialized(_this), "setUnsubmittedValues", function (values) {
      _this.unsubmittedValues = omit(Object.keys(_this.state.values), values);
    });

    _defineProperty(_assertThisInitialized(_this), "setInitialValues", function (defaults) {
      var values = _this.state.values; // Merge defaults with values and unsubmitted values from Wizard

      return _objectSpread({}, defaults, pick(Object.keys(defaults), _this.unsubmittedValues), values);
    });

    _defineProperty(_assertThisInitialized(_this), "previous", function (values) {
      var currentPage = Math.max(_this.state.currentPage - 1, 0);

      if (isPlainObject(values)) {
        _this.setValues(values);
      }

      _this.navigateTo(currentPage);
    });

    _defineProperty(_assertThisInitialized(_this), "next", function (values) {
      var lastPage = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
      var children = _this.props.children;

      _this.setValues(values);

      if (!lastPage) {
        var wizardPages = _this.getWizardPages(children);

        var pageCount = wizardPages.length;
        var currentPage = Math.min(_this.state.currentPage + 1, pageCount - 1);

        _this.setState(function () {
          return {
            lastValidPage: currentPage - 1
          };
        });

        _this.navigateTo(currentPage);
      }
    });

    _defineProperty(_assertThisInitialized(_this), "handleNext", function () {
      var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
          values = _ref.values;

      var _this$props = _this.props,
          children = _this$props.children,
          onSubmit = _this$props.onSubmit;
      var currentPage = _this.state.currentPage;
      var pageCount = _this.getWizardPages(children).length - 1;
      var isLastPage = currentPage === pageCount;

      _this.next(values && !values.currentTarget ? values : {}, isLastPage);

      if (isLastPage) {
        eventBus.dispatch({
          type: 'onSubmit',
          component: 'Wizard',
          values: _this.state.values
        });
        return onSubmit(_this.state.values, _this.props.history);
      }
    });

    _defineProperty(_assertThisInitialized(_this), "navigateTo", function (index, values) {
      _this.setState({
        currentPage: index,
        lastVisitedPage: _this.state.currentPage
      }, function () {
        if (_this.props.history) {
          _this.props.history.push({
            pathname: _this.props.basePath,
            search: window.location.search,
            state: {
              currentPage: index
            }
          });
        }

        _this.onNavigate();
      }); // If optional values are provided then add them to the state


      if (values) {
        _this.setValues(values);
      }
    });

    _defineProperty(_assertThisInitialized(_this), "onNavigate", function () {
      var navigationInfo = {
        currentPageIndex: _this.state.currentPage,
        values: _this.state.values
      };
      var currentPageName = get("[".concat(_this.state.currentPage, "].props.name"), _this.getWizardPages(_this.props.children));

      if (currentPageName) {
        navigationInfo.currentPageName = currentPageName;
      }

      eventBus.dispatch(_objectSpread({
        type: 'onNavigate',
        component: 'Wizard'
      }, navigationInfo));

      if (_this.props.onNavigate) {
        _this.props.onNavigate(navigationInfo);
      }
    });

    _defineProperty(_assertThisInitialized(_this), "getWizardComponents", function (children) {
      return React.Children.toArray(children).filter(function (child) {
        return child.type !== WizardPage;
      });
    });

    _defineProperty(_assertThisInitialized(_this), "getWizardPages", function (children) {
      return React.Children.toArray(children).filter(function (child) {
        return child.type === WizardPage;
      });
    });

    var _currentPage = 0;
    _this.state = {
      currentPage: _currentPage,
      lastVisitedPage: null,
      lastValidPage: null,
      values: props.initialValues ? props.initialValues : {}
    };
    _this.unsubmittedValues = {};

    if (props.history) {
      _this.unlisten = _this.props.history.listen(_this.handleHistoryListen);
    }

    _this.ref = createRef();
    return _this;
  }

  _createClass(Wizard, [{
    key: "render",
    value: function render() {
      var _this2 = this;

      var _this$props2 = this.props,
          children = _this$props2.children,
          disablePrevious = _this$props2.disablePrevious;
      var _this$state2 = this.state,
          currentPage = _this$state2.currentPage,
          values = _this$state2.values;
      var wizardPages = this.getWizardPages(children);
      var activePage = wizardPages[currentPage];
      var wizardComponents = this.getWizardComponents(children);
      var isLastPage = currentPage === wizardPages.length - 1;
      return React.createElement("div", {
        ref: this.ref
      }, React.createElement(Provider, {
        value: {
          activePage: activePage,
          currentPage: this.state.currentPage,
          lastVisitedPage: this.state.lastVisitedPage,
          disablePrevious: disablePrevious,
          onNext: this.handleNext,
          onPrevious: this.previous,
          onSubmit: this.handleNext,
          isLastPage: isLastPage,
          navigateTo: this.navigateTo,
          onChange: this.handleChange,
          setInitialValues: function setInitialValues(defaults) {
            return _this2.setInitialValues(defaults);
          },
          setValues: this.setValues,
          setUnsubmittedValues: this.setUnsubmittedValues,
          totalPages: wizardPages.length,
          values: values
        }
      }, wizardComponents, activePage));
    }
  }]);

  return Wizard;
}(Component);

_defineProperty(Wizard, "defaultProps", {
  basePath: '',
  disablePrevious: false,
  history: null
});

Wizard.propTypes = process.env.NODE_ENV !== "production" ? {
  basePath: PropTypes.string,
  children: PropTypes.node.isRequired,
  disablePrevious: PropTypes.bool,
  history: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  initialValues: PropTypes.object,

  /**
   * proxied to eventBus
   */
  onSubmit: PropTypes.func.isRequired,

  /**
   * proxied to eventBus
   */
  onNavigate: PropTypes.func
} : {};
export default Wizard;